import {
    Component,
    effect,
    ElementRef,
    OnDestroy,
    ViewChild,
    ChangeDetectionStrategy,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from '../../store';
import {
    $insightsDashboards,
    $insightsLoading,
    $insightsToken,
} from '../../store/insights/insights.selectors';
import { FormBuilder } from '@angular/forms';
import { filter, first, Observable, Subject, takeUntil } from 'rxjs';
import { getInsightsGuestTokenRequestAction } from '../../store/insights/insights.actions';
import { embedDashboard, EmbeddedDashboard } from '@superset-ui/embedded-sdk';
import { ActivatedRoute, Router } from '@angular/router';

@Component({
    selector: 'myrtls-insights-dashboards',
    templateUrl: './insights-dashboards.component.html',
    styleUrls: [],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class InsightsDashboardsComponent implements OnDestroy {
    private readonly unsubscribe$ = new Subject<void>();

    $loading = this.store.select($insightsLoading);
    $dashboards = this.store.selectSignal($insightsDashboards);
    $token = this.store.select($insightsToken);

    dashboard = this.formBuilder.control<string | null>(null);
    url = '';

    siteIdRegexp = /^S\d+ - /;

    embeddedDashboard: EmbeddedDashboard | null = null;

    @ViewChild('supersetDashboard')
    supersetDashboardDiv?: ElementRef<HTMLDivElement>;

    constructor(
        private readonly store: Store<AppState>,
        private formBuilder: FormBuilder,
        private router: Router,
        private activatedRoute: ActivatedRoute,
    ) {
        effect(
            () => {
                const { dashboards, url } = this.$dashboards();

                if (
                    dashboards.length > 0 &&
                    url &&
                    this.dashboard.value === null
                ) {
                    this.url = url;

                    const selectedDashboardIdFromQuery =
                        this.activatedRoute.snapshot.queryParams['id'];

                    if (selectedDashboardIdFromQuery) {
                        this.dashboard.setValue(selectedDashboardIdFromQuery);
                        return;
                    }

                    const selectedDashboardIdFromLocalStorage =
                        localStorage.getItem('selected-insights-dashboard-id');

                    this.dashboard.setValue(
                        selectedDashboardIdFromLocalStorage || dashboards[0].id,
                    );
                }
            },
            { allowSignalWrites: true },
        );

        this.dashboard.valueChanges
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(async id => {
                const dashboards = this.$dashboards().dashboards;

                if (dashboards.length === 0 || this.url === '') {
                    return;
                }

                const dashboard = dashboards.find(
                    dashboard => dashboard.id === id,
                );

                if (!dashboard) {
                    this.dashboard.setValue(dashboards[0].id);
                    return;
                }

                localStorage.setItem(
                    'selected-insights-dashboard-id',
                    dashboard.id.toString(),
                );

                console.log('Adding parameters to URL...', {
                    id: dashboard.id,
                });
                this.router.navigate([], {
                    queryParams: {
                        id: dashboard.id,
                    },
                    queryParamsHandling: 'merge',
                    relativeTo: this.activatedRoute,
                });

                const mountPoint = this.supersetDashboardDiv?.nativeElement;

                if (!mountPoint) {
                    return;
                }

                this.unmountDashboard();

                this.embeddedDashboard = await embedDashboard({
                    id: dashboard.embedding,
                    mountPoint,
                    fetchGuestToken: () => this.refreshGuestToken(this.$token),
                    supersetDomain: this.url,
                    iframeSandboxExtras: ['allow-popups-to-escape-sandbox'],
                    dashboardUiConfig: {
                        hideTitle: false,
                        hideChartControls: false,
                        filters: {
                            expanded: false,
                        },
                    },
                });

                const iframe = document.querySelector('iframe');

                if (iframe) {
                    iframe.style.width = '100%';
                    iframe.style.minHeight = '100%';
                }
            });
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
        this.unmountDashboard();
    }

    unmountDashboard() {
        if (this.embeddedDashboard) {
            this.embeddedDashboard.unmount();
            this.embeddedDashboard = null;
        }
    }

    async refreshGuestToken(token: Observable<string | null>) {
        return new Promise<string>(resolve => {
            token
                .pipe(
                    filter(token => token !== null),
                    first(),
                )
                .subscribe(token => {
                    resolve(token as string);
                });

            this.store.dispatch(getInsightsGuestTokenRequestAction());
        });
    }

    useFullscreenDashboard(): void {
        if (!this.supersetDashboardDiv) {
            return;
        }

        const nativeDiv = this.supersetDashboardDiv.nativeElement;

        if (nativeDiv.requestFullscreen) {
            nativeDiv.requestFullscreen();
        }
    }
}
